import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { formatarMoeda } from '../utils/formatters';

/* ══════════════════════════════════════════════════
   HELPERS — Cores e branding
══════════════════════════════════════════════════ */

/**
 * Converte hex (#0B1D35) para [r, g, b]
 */
function hexToRgb(hex) {
  const h = (hex || '#0B1D35').replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

/**
 * Carrega imagem de URL e retorna base64 para jsPDF
 * Funciona com URLs do Firebase Storage (CORS ok se mesma origem)
 */
async function loadImageAsBase64(url) {
  if (!url) return null;
  try {
    return await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve({
          data: canvas.toDataURL('image/png'),
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  } catch {
    return null;
  }
}

/**
 * Desenha o header com branding no PDF
 * @param {jsPDF} pdf
 * @param {object} brand - { logoBase64, nomeEmpresa, corPrimaria, corAcento }
 * @param {string[]} subtitles - linhas de subtítulo
 * @param {number} headerHeight
 * @param {'p'|'l'} orientation
 */
async function drawBrandedHeader(pdf, brand, subtitles = [], headerHeight = 30, orientation = 'p') {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const [r, g, b] = hexToRgb(brand.corPrimaria);

  // Background
  pdf.setFillColor(r, g, b);
  pdf.rect(0, 0, pdfWidth, headerHeight, 'F');
  pdf.setTextColor(255, 255, 255);

  let xStart = 15;

  // Logo
  if (brand.logoBase64) {
    const logoMaxH = headerHeight - 8;
    const ratio = brand.logoBase64.width / brand.logoBase64.height;
    const logoH = Math.min(logoMaxH, 18);
    const logoW = logoH * ratio;

    try {
      pdf.addImage(
        brand.logoBase64.data, 'PNG',
        12, (headerHeight - logoH) / 2,
        logoW, logoH
      );
      xStart = 12 + logoW + 6;
    } catch {
      // Fallback: sem logo
    }
  }

  // Nome da empresa
  pdf.setFontSize(orientation === 'l' ? 14 : 16);
  const nameY = subtitles.length > 0
    ? headerHeight / 2 - (subtitles.length * 2.5)
    : headerHeight / 2 + 2;

  pdf.text(brand.nomeEmpresa || 'SimulaBank', xStart, nameY);

  // Subtítulos (data, valor, prazo etc)
  pdf.setFontSize(9);
  subtitles.forEach((line, i) => {
    pdf.text(line, xStart, nameY + 6 + i * 5);
  });

  // Linha de destaque (accent) no bottom
  const [ar, ag, ab] = hexToRgb(brand.corAcento);
  pdf.setFillColor(ar, ag, ab);
  pdf.rect(0, headerHeight, pdfWidth, 1.5, 'F');
}

/**
 * Desenha footer em todas as páginas
 */
function drawBrandedFooter(pdf, brand) {
  const totalPages = pdf.internal.getNumberOfPages();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const pdfWidth = pdf.internal.pageSize.getWidth();

  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(7);
    pdf.setTextColor(150, 150, 150);
    pdf.text(
      `Simulação para fins informativos. Sujeito à análise de crédito. | ${brand.nomeEmpresa || 'SimulaBank'} ${new Date().getFullYear()}`,
      pdfWidth / 2, pageHeight - 5, { align: 'center' }
    );
    pdf.text(`Página ${i} de ${totalPages}`, pdfWidth - 15, pageHeight - 5, { align: 'right' });
  }
}

/**
 * Prepara dados de branding a partir do account
 * @param {object} account - do ThemeContext/accountService
 * @returns {Promise<object>} brand object
 */
export async function prepareBrand(account) {
  const logoBase64 = account?.logoUrl
    ? await loadImageAsBase64(account.logoUrl)
    : null;

  return {
    logoBase64,
    nomeEmpresa: account?.nomeEmpresa || 'SimulaBank',
    corPrimaria: account?.corPrimaria || '#0B1D35',
    corAcento: account?.corAcento || '#00D68F',
  };
}

/* ══════════════════════════════════════════════════
   PDF BANCO — Comparativo + tabela de parcelas
══════════════════════════════════════════════════ */

/**
 * @param {object} params
 * @param {object} params.simulacaoAtual
 * @param {number} params.valorFinanciado
 * @param {number} params.prazoMeses
 * @param {React.RefObject} params.comparativoRef
 * @param {object} [params.account] - dados da conta para branding
 * @returns {Promise<{blob: Blob, fileName: string} | null>}
 */
export async function exportarPDFBanco({ simulacaoAtual, valorFinanciado, prazoMeses, comparativoRef, account }) {
  if (!comparativoRef?.current || !simulacaoAtual) return null;

  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const brand = await prepareBrand(account);
    const [pr, pg, pb] = hexToRgb(brand.corPrimaria);
    const [ar, ag, ab] = hexToRgb(brand.corAcento);

    // ============ PÁGINA 1 - COMPARATIVO ============
    await drawBrandedHeader(pdf, brand, [
      `Gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`,
      `Valor Financiado: ${formatarMoeda(valorFinanciado)} | Prazo: ${prazoMeses} meses`,
    ], 30, 'p');

    const canvasComparativo = await html2canvas(comparativoRef.current, {
      scale: 2, backgroundColor: '#F7F8FA', logging: false,
    });

    const imgComparativo = canvasComparativo.toDataURL('image/png');
    const imgHeight = (canvasComparativo.height * (pdfWidth - 20)) / canvasComparativo.width;
    pdf.addImage(imgComparativo, 'PNG', 10, 36, pdfWidth - 20, imgHeight);

    // ============ PÁGINA 2+ - PARCELAS ============
    pdf.addPage();

    pdf.setFillColor(pr, pg, pb);
    pdf.rect(0, 0, pdfWidth, 20, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(14);
    pdf.text(`Evolução das Parcelas - ${simulacaoAtual.nomeBanco}`, pdfWidth / 2, 13, { align: 'center' });

    // Linha accent
    pdf.setFillColor(ar, ag, ab);
    pdf.rect(0, 20, pdfWidth, 1.5, 'F');

    pdf.setFontSize(8);
    let yPos = 30;
    const colWidths = [12, 28, 28, 22, 22, 18, 28, 32];
    const headers = ['Nº', 'Amortização', 'Juros', 'MIP', 'DFI', 'TGC', 'Parcela', 'Saldo Devedor'];

    const drawTableHeader = () => {
      pdf.setFillColor(ar, ag, ab);
      pdf.rect(10, yPos - 5, pdfWidth - 20, 8, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(7);
      let xPos = 12;
      headers.forEach((header, i) => {
        pdf.text(header, xPos, yPos);
        xPos += colWidths[i];
      });
      yPos += 6;
      pdf.setTextColor(50, 50, 50);
    };

    drawTableHeader();

    simulacaoAtual.parcelas.forEach((parcela, idx) => {
      if (yPos > pageHeight - 20) {
        pdf.addPage();
        yPos = 20;
        drawTableHeader();
      }

      if (idx % 2 === 0) {
        pdf.setFillColor(245, 245, 245);
        pdf.rect(10, yPos - 4, pdfWidth - 20, 6, 'F');
      }

      let xPos = 12;
      const valores = [
        parcela.numero === 0 ? '0' : parcela.numero.toString(),
        formatarMoeda(parcela.amortizacao),
        formatarMoeda(parcela.juros),
        formatarMoeda(parcela.mip),
        formatarMoeda(parcela.dfi),
        formatarMoeda(parcela.tgc),
        formatarMoeda(parcela.valorParcela),
        formatarMoeda(parcela.saldoDevedor),
      ];

      pdf.setFontSize(7);
      pdf.setTextColor(50, 50, 50);
      valores.forEach((valor, i) => {
        pdf.text(valor, xPos, yPos);
        xPos += colWidths[i];
      });
      yPos += 6;
    });

    // Totais
    yPos += 5;
    pdf.setFillColor(pr, pg, pb);
    pdf.rect(10, yPos - 4, pdfWidth - 20, 10, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(9);
    pdf.text(
      `TOTAIS:  Juros: ${formatarMoeda(simulacaoAtual.totais.totalJuros)}  |  Seguros: ${formatarMoeda(simulacaoAtual.totais.totalMIP + simulacaoAtual.totais.totalDFI)}  |  Total a Pagar: ${formatarMoeda(simulacaoAtual.totais.totalAPagar)}`,
      15, yPos + 2
    );

    drawBrandedFooter(pdf, brand);

    const fileName = `simulacao-${simulacaoAtual.nomeBanco.split(' ')[0]}-${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);

    const blob = pdf.output('blob');
    return { blob, fileName };
  } catch (error) {
    console.error('Erro ao gerar PDF:', error);
    alert('Erro ao gerar PDF. Tente novamente.');
    return null;
  }
}

/* ══════════════════════════════════════════════════
   PDF EVOLUÇÃO — Comparativo multibanco (landscape)
══════════════════════════════════════════════════ */

/**
 * @param {object} params
 * @param {object} params.resultado
 * @param {number} params.valorFinanciado
 * @param {number} params.valorImovel
 * @param {number} params.prazoMeses
 * @param {object} [params.account] - dados da conta para branding
 * @returns {Promise<{blob: Blob, fileName: string} | null>}
 */
export async function exportarPDFEvolucao({ resultado, valorFinanciado, valorImovel, prazoMeses, account }) {
  if (!resultado?.simulacoes) return null;

  try {
    const pdf = new jsPDF('l', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const bancos = resultado.simulacoes;
    const numBancos = bancos.length;
    const brand = await prepareBrand(account);
    const [pr, pg, pb] = hexToRgb(brand.corPrimaria);
    const [ar, ag, ab] = hexToRgb(brand.corAcento);

    // ============ PÁGINA 1 - RESUMO ============
    await drawBrandedHeader(pdf, brand, [
      `Gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`,
      `Valor Financiado: ${formatarMoeda(valorFinanciado)} | Prazo: ${prazoMeses} meses | Valor do Imóvel: ${formatarMoeda(valorImovel)}`,
    ], 25, 'l');

    let yPos = 35;

    pdf.setFontSize(11);
    pdf.setTextColor(pr, pg, pb);
    pdf.text('Resumo da Simulação', 15, yPos);
    yPos += 8;

    const colWidthResumo = (pdfWidth - 60) / numBancos;
    pdf.setFillColor(pr, pg, pb);
    pdf.rect(15, yPos - 5, pdfWidth - 30, 8, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(8);
    pdf.text('Resumo', 17, yPos);
    bancos.forEach((b, i) => {
      pdf.text(b.nomeBanco.split(' ')[0], 50 + i * colWidthResumo, yPos);
    });
    yPos += 8;

    const linhasResumo = [
      { label: 'Taxa de Juros', valores: bancos.map((b) => `${b.taxaJurosAnual.toFixed(2)}%`) },
      { label: 'Primeira Parcela', valores: bancos.map((b) => formatarMoeda(b.informacoes.primeiraParcela)) },
      { label: 'Última Parcela', valores: bancos.map((b) => formatarMoeda(b.informacoes.ultimaParcela)) },
      { label: 'Total Juros', valores: bancos.map((b) => formatarMoeda(b.totais.totalJuros)) },
      { label: 'Total Seguros', valores: bancos.map((b) => formatarMoeda(b.totais.totalMIP + b.totais.totalDFI)) },
      { label: 'Total a Pagar', valores: bancos.map((b) => formatarMoeda(b.totais.totalAPagar)) },
    ];

    const menorTotal = Math.min(...bancos.map((b) => b.totais.totalAPagar));

    linhasResumo.forEach((linha, idx) => {
      if (idx % 2 === 0) {
        pdf.setFillColor(240, 240, 240);
        pdf.rect(15, yPos - 4, pdfWidth - 30, 7, 'F');
      }
      pdf.setTextColor(100, 100, 100);
      pdf.text(linha.label, 17, yPos);

      linha.valores.forEach((valor, i) => {
        const isMelhor = linha.label === 'Total a Pagar' && bancos[i].totais.totalAPagar === menorTotal;
        if (isMelhor) {
          pdf.setTextColor(ar, ag, ab);
          pdf.text(valor + ' ✓', 50 + i * colWidthResumo, yPos);
        } else {
          pdf.setTextColor(50, 50, 50);
          pdf.text(valor, 50 + i * colWidthResumo, yPos);
        }
      });
      yPos += 7;
    });

    // Soma por período
    yPos += 10;
    pdf.setFontSize(11);
    pdf.setTextColor(pr, pg, pb);
    pdf.text('Soma de Valores por Período (12 meses)', 15, yPos);
    yPos += 8;

    pdf.setFillColor(pr, pg, pb);
    pdf.rect(15, yPos - 5, pdfWidth - 30, 8, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(8);
    pdf.text('Período', 17, yPos);
    bancos.forEach((b, i) => {
      pdf.text(b.nomeBanco.split(' ')[0], 50 + i * colWidthResumo, yPos);
    });
    yPos += 8;

    const numPeriodos = Math.ceil(prazoMeses / 12);
    for (let p = 0; p < numPeriodos; p++) {
      const inicio = p * 12 + 1;
      const fim = Math.min((p + 1) * 12, prazoMeses);

      if (yPos > pageHeight - 20) {
        pdf.addPage();
        yPos = 20;
      }

      if (p % 2 === 0) {
        pdf.setFillColor(240, 240, 240);
        pdf.rect(15, yPos - 4, pdfWidth - 30, 7, 'F');
      }

      pdf.setTextColor(100, 100, 100);
      pdf.text(`Parcela ${String(inicio).padStart(3, '0')} - ${String(fim).padStart(3, '0')}`, 17, yPos);

      const somas = bancos.map((b) =>
        b.parcelas
          .filter((pa) => pa.numero >= inicio && pa.numero <= fim)
          .reduce((acc, pa) => acc + pa.valorParcela, 0)
      );
      const menorSoma = Math.min(...somas.filter((s) => s > 0));

      somas.forEach((soma, i) => {
        const isMenor = soma === menorSoma && soma > 0;
        if (isMenor) {
          pdf.setTextColor(ar, ag, ab);
        } else {
          pdf.setTextColor(50, 50, 50);
        }
        pdf.text(formatarMoeda(soma), 50 + i * colWidthResumo, yPos);
      });
      yPos += 7;
    }

    // Evolução parcelas
    pdf.addPage();

    const parcelasFiltradas = bancos.map((b) => ({
      ...b,
      parcelas: b.parcelas.filter((p) => p.numero > 0),
    }));

    // Criar mapa por número de parcela para cada banco (alinha corretamente)
    const parcelaMaps = parcelasFiltradas.map((b) => {
      const map = {};
      b.parcelas.forEach((p) => { map[p.numero] = p; });
      return map;
    });

    // Descobrir todos os números de parcela existentes (union de todos os bancos)
    const allNumeros = new Set();
    parcelasFiltradas.forEach((b) => {
      b.parcelas.forEach((p) => allNumeros.add(p.numero));
    });
    const numerosOrdenados = [...allNumeros].sort((a, b) => a - b);
    const totalParcelas = numerosOrdenados.length;

    const parcelasPorPagina = 50; // máximo seguro, o controle real é por yPos
    let parcelaAtual = 0;

    while (parcelaAtual < totalParcelas) {
      if (parcelaAtual > 0) pdf.addPage();

      pdf.setFillColor(pr, pg, pb);
      pdf.rect(0, 0, pdfWidth, 15, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(12);
      pdf.text('Evolução das Parcelas - Todos os Bancos', pdfWidth / 2, 10, { align: 'center' });

      // Linha accent
      pdf.setFillColor(ar, ag, ab);
      pdf.rect(0, 15, pdfWidth, 1.5, 'F');

      yPos = 25;

      pdf.setFillColor(ar, ag, ab);
      pdf.rect(10, yPos - 5, pdfWidth - 20, 8, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);

      const colWidthParcela = (pdfWidth - 40) / numBancos;
      pdf.text('Parcela', 12, yPos);
      parcelasFiltradas.forEach((b, i) => {
        pdf.text(b.nomeBanco.split(' ')[0], 35 + i * colWidthParcela, yPos);
      });
      yPos += 8;

      while (parcelaAtual < totalParcelas && yPos < pageHeight - 15) {
        const numeroParcela = numerosOrdenados[parcelaAtual];

        if (parcelaAtual % 2 === 0) {
          pdf.setFillColor(245, 245, 245);
          pdf.rect(10, yPos - 4, pdfWidth - 20, 6, 'F');
        }

        pdf.setTextColor(100, 100, 100);
        pdf.text(String(numeroParcela).padStart(3, '0'), 12, yPos);

        // Buscar valor de cada banco pelo número da parcela (não pelo índice)
        const valoresParcela = parcelaMaps.map((map) => map[numeroParcela]?.valorParcela || 0);
        const menorValor = Math.min(...valoresParcela.filter((v) => v > 0));

        parcelaMaps.forEach((map, i) => {
          const parcela = map[numeroParcela];
          const isMenor = parcela?.valorParcela === menorValor && parcela?.valorParcela > 0;
          if (isMenor) {
            pdf.setTextColor(ar, ag, ab);
          } else {
            pdf.setTextColor(50, 50, 50);
          }
          pdf.text(parcela ? formatarMoeda(parcela.valorParcela) : '-', 35 + i * colWidthParcela, yPos);
        });

        yPos += 6;
        parcelaAtual++;
      }
    }

    drawBrandedFooter(pdf, brand);

    const fileName = `simulacao-comparativo-${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);

    const blob = pdf.output('blob');
    return { blob, fileName };
  } catch (error) {
    console.error('Erro ao gerar PDF:', error);
    alert('Erro ao gerar PDF. Tente novamente.');
    return null;
  }
}